/**
 * Diary — Working Hours.
 *
 * Manage each consultant's weekly working template, which is what the slot
 * engine (DiarySlotService) turns into bookable appointment slots. Without an
 * active schedule the "any consultant" slot query returns nothing, which is
 * why the booking modal shows "no bookable slots".
 *
 * Backend contract (/v1/diary/schedules — Super Admin / Dealer Principal /
 * Sales Manager):
 *   GET    /v1/diary/schedules
 *   POST   /v1/diary/schedules      create-or-replace, keyed by consultantId
 *   DELETE /v1/diary/schedules/{id} consultant falls back to the default template
 *
 * ScheduleDTO: { id, consultantId, weeklyTemplate, slotMinutes, maxConcurrent, active }
 * weeklyTemplate: { "MON":[{"start":"09:00","end":"17:30"}], "TUE":[...], … }
 *   — a missing day key = day off. An empty template = the engine's default
 *     (Mon–Sat 08:30–17:30).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { appointmentsService } from '../lib/services';
import { useConsultantOptions } from '../lib/useDirectory';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';

const DAYS = [
  ['MON', 'Monday'],
  ['TUE', 'Tuesday'],
  ['WED', 'Wednesday'],
  ['THU', 'Thursday'],
  ['FRI', 'Friday'],
  ['SAT', 'Saturday'],
  ['SUN', 'Sunday'],
];

const DEFAULT_START = '09:00';
const DEFAULT_END   = '17:30';

// The engine's implicit fallback when a consultant has NO schedule row.
const ENGINE_DEFAULT = 'Default · Mon–Sat 08:30–17:30';

const blankDays = () =>
  Object.fromEntries(DAYS.map(([k]) => [k, { enabled: false, start: DEFAULT_START, end: DEFAULT_END }]));

/** ScheduleDTO.weeklyTemplate → editor day-state. */
function templateToDays(template) {
  const days = blankDays();
  if (!template || typeof template !== 'object') return days;
  for (const [k] of DAYS) {
    const windows = template[k];
    if (Array.isArray(windows) && windows.length) {
      const w = windows[0] || {};
      days[k] = {
        enabled: true,
        start: typeof w.start === 'string' ? w.start : DEFAULT_START,
        end:   typeof w.end === 'string' ? w.end : DEFAULT_END,
      };
    }
  }
  return days;
}

/** Editor day-state → weeklyTemplate for the API. */
function daysToTemplate(days) {
  const t = {};
  for (const [k] of DAYS) {
    const d = days[k];
    if (d?.enabled) t[k] = [{ start: d.start, end: d.end }];
  }
  return t;
}

/** One-line summary of working days for the list. */
function summarise(template) {
  if (!template || Object.keys(template).length === 0) return null;
  const on = DAYS.filter(([k]) => Array.isArray(template[k]) && template[k].length);
  if (!on.length) return 'No working days set';
  return on
    .map(([k, label]) => {
      const w = template[k][0];
      return `${label.slice(0, 3)} ${w.start}–${w.end}`;
    })
    .join(' · ');
}

export default function DiarySettings() {
  return <WorkingHoursPanel />;
}

/**
 * Working-hours manager. Rendered standalone at /diary-settings (with its own
 * TopBar) and embedded inside Settings → Working hours (`embedded`, no TopBar,
 * since the Settings shell already provides the chrome).
 */
export function WorkingHoursPanel({ embedded = false }) {
  const { toast } = useToast();
  const { options: consultants, loading: loadingConsultants, error: consultantError } = useConsultantOptions();

  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [editing, setEditing] = useState(null);   // consultant option being edited

  const loadSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    try {
      const rows = await appointmentsService.schedules();
      setSchedules(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const scheduleFor = useCallback(
    (consultantId) => schedules.find(s => s.consultantId === consultantId) || null,
    [schedules],
  );

  const activeCount = useMemo(
    () => schedules.filter(s => s.active !== false && summarise(s.weeklyTemplate)).length,
    [schedules],
  );

  const onDelete = async (schedule) => {
    try {
      await appointmentsService.deleteSchedule(schedule.id);
      toast('Schedule removed — consultant falls back to the default template', { variant: 'info' });
      loadSchedules();
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not remove the schedule'), { variant: 'danger' });
    }
  };

  return (
    <>
      {!embedded && (
        <TopBar
          crumbs={[['Appointments', '/appointments'], 'Working hours']}
          right={
            <span className="mono text-xs text-muted">
              {activeCount} consultant{activeCount === 1 ? '' : 's'} bookable
            </span>
          }
        />
      )}
      <div className={embedded ? 'flex-col' : 'app-content'} style={embedded ? { gap: 0 } : undefined}>
        {embedded && (
          <div className="flex-between" style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Working hours</div>
            <span className="mono text-xs text-muted">
              {activeCount} consultant{activeCount === 1 ? '' : 's'} bookable
            </span>
          </div>
        )}
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-label">WHY THIS MATTERS</div>
          <div className="text-xs text-muted" style={{ lineHeight: 1.7, marginTop: 4 }}>
            Bookable appointment slots are generated from these weekly templates. A consultant needs an
            <strong> active</strong> schedule with at least one working day before their slots appear in the
            booking screen. Consultants with no schedule fall back to <em>{ENGINE_DEFAULT}</em> when booked
            directly, but won't show up in the "any consultant" slot list until a schedule is saved.
          </div>
        </div>

        {consultantError && (
          <div className="card" style={{ marginBottom: 14, borderColor: 'var(--warning)' }}>
            <div className="text-xs" style={{ color: 'var(--warning)', lineHeight: 1.6 }}>
              Couldn't load the consultant directory. You need the Sales Manager, Dealer Principal or
              Super Admin role to manage working hours.
            </div>
          </div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Consultant</th>
                <th>Working hours</th>
                <th>Slot</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(loadingConsultants || loadingSchedules) && (
                <tr><td colSpan={5}>
                  <div className="mono text-xs text-muted" style={{ padding: 16 }}>Loading…</div>
                </td></tr>
              )}

              {!loadingConsultants && !loadingSchedules && consultants.length === 0 && (
                <tr><td colSpan={5}>
                  <div className="empty-state" style={{ padding: 32 }}>
                    <div className="title">No consultants found</div>
                    <div className="desc">Invite team members under Settings → Roles &amp; teams first.</div>
                  </div>
                </td></tr>
              )}

              {!loadingConsultants && consultants.map((c) => {
                const sched = scheduleFor(c.id);
                const summary = sched ? summarise(sched.weeklyTemplate) : null;
                const inactive = sched && sched.active === false;
                const bookable = sched && sched.active !== false && !!summary;

                return (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.primary}</div>
                      <div className="mono text-xs text-muted">{c.secondary}</div>
                    </td>
                    <td className="mono text-xs text-muted" style={{ maxWidth: 320 }}>
                      {sched
                        ? (summary || 'No working days set')
                        : <span title="No schedule row — the engine's default applies when booked directly">{ENGINE_DEFAULT}</span>}
                    </td>
                    <td className="mono text-xs text-muted">
                      {sched ? `${sched.slotMinutes ?? 30}m` : '30m'}
                      {sched && sched.maxConcurrent > 1 && ` ·×${sched.maxConcurrent}`}
                    </td>
                    <td>
                      {bookable ? (
                        <span className="pill success filled">Bookable</span>
                      ) : inactive ? (
                        <span className="pill danger filled">Inactive</span>
                      ) : sched ? (
                        <span className="pill warning filled">No days</span>
                      ) : (
                        <span className="pill filled" style={{ background: 'var(--bg-glass)' }}>Default</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button className="btn small" onClick={() => setEditing(c)}>
                        {sched ? 'Edit' : 'Set hours'}
                      </button>
                      {sched && (
                        <button
                          className="btn small danger"
                          style={{ marginLeft: 6 }}
                          onClick={() => onDelete(sched)}
                          title="Remove this schedule"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {editing && (
        <ScheduleEditor
          consultant={editing}
          schedule={scheduleFor(editing.id)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadSchedules(); }}
        />
      )}
    </>
  );
}

/* ── editor modal ── */
function ScheduleEditor({ consultant, schedule, onClose, onSaved }) {
  const { toast } = useToast();
  const [days, setDays] = useState(() => templateToDays(schedule?.weeklyTemplate));
  const [slotMinutes, setSlotMinutes] = useState(schedule?.slotMinutes ?? 30);
  const [maxConcurrent, setMaxConcurrent] = useState(schedule?.maxConcurrent ?? 1);
  const [active, setActive] = useState(schedule?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setDay = (k, patch) => setDays((d) => ({ ...d, [k]: { ...d[k], ...patch } }));

  const applyPreset = (preset) => {
    setDays((d) => {
      const next = { ...d };
      const set = (keys, on) => keys.forEach((k) => {
        next[k] = { ...next[k], enabled: on, start: next[k].start, end: next[k].end };
      });
      if (preset === 'weekdays') {
        set(['MON', 'TUE', 'WED', 'THU', 'FRI'], true);
        set(['SAT', 'SUN'], false);
      } else if (preset === 'sixday') {
        set(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'], true);
        set(['SUN'], false);
      } else if (preset === 'clear') {
        DAYS.forEach(([k]) => { next[k] = { ...next[k], enabled: false }; });
      }
      return next;
    });
  };

  /** Copy the first enabled day's times to every enabled day. */
  const copyTimesDown = () => {
    const first = DAYS.map(([k]) => k).find((k) => days[k].enabled);
    if (!first) return;
    const { start, end } = days[first];
    setDays((d) => {
      const next = { ...d };
      DAYS.forEach(([k]) => { if (next[k].enabled) next[k] = { ...next[k], start, end }; });
      return next;
    });
  };

  const enabledDays = DAYS.filter(([k]) => days[k].enabled);
  const invalidDay = enabledDays.find(([k]) => days[k].end <= days[k].start);
  const canSave = !busy && !invalidDay;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      // POST is create-or-replace keyed by consultantId, so it handles both
      // a brand-new schedule and editing an existing one.
      await appointmentsService.createSchedule({
        consultantId: consultant.id,
        weeklyTemplate: daysToTemplate(days),
        slotMinutes: Number(slotMinutes),
        maxConcurrent: Number(maxConcurrent),
        active,
      });
      toast(`Working hours saved for ${consultant.primary}`, { variant: 'success' });
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the schedule'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="sched-title" onClick={onClose}>
      <form className="modal" style={{ width: 'min(600px, 94vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="modal-header">
          <div>
            <div id="sched-title" style={{ fontSize: 16, fontWeight: 600 }}>Working hours</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{consultant.primary}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Presets */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="mono text-xs text-muted">PRESET:</span>
            <button type="button" className="btn small" onClick={() => applyPreset('weekdays')}>Mon–Fri</button>
            <button type="button" className="btn small" onClick={() => applyPreset('sixday')}>Mon–Sat</button>
            <button type="button" className="btn small" onClick={() => applyPreset('clear')}>Clear</button>
            <button type="button" className="btn small" onClick={copyTimesDown} title="Copy the first working day's times to all working days">
              Copy times down
            </button>
          </div>

          {/* Per-day rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {DAYS.map(([k, label]) => {
              const d = days[k];
              const bad = d.enabled && d.end <= d.start;
              return (
                <div
                  key={k}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '130px 1fr 1fr',
                    gap: 10, alignItems: 'center',
                    padding: '4px 0',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={d.enabled}
                      onChange={(e) => setDay(k, { enabled: e.target.checked })}
                    />
                    {label}
                  </label>
                  <input
                    type="time"
                    value={d.start}
                    disabled={!d.enabled}
                    onChange={(e) => setDay(k, { start: e.target.value })}
                    aria-label={`${label} start`}
                    style={{ opacity: d.enabled ? 1 : 0.4 }}
                  />
                  <input
                    type="time"
                    value={d.end}
                    disabled={!d.enabled}
                    onChange={(e) => setDay(k, { end: e.target.value })}
                    aria-label={`${label} end`}
                    style={{ opacity: d.enabled ? 1 : 0.4, borderColor: bad ? 'var(--danger)' : undefined }}
                  />
                </div>
              );
            })}
          </div>

          {invalidDay && (
            <div className="text-xs" style={{ color: 'var(--danger)' }}>
              End time must be after start time on {DAYS.find(([k]) => k === invalidDay[0])[1]}.
            </div>
          )}

          {/* Slot config */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="sched-slot">Slot length</label>
              <select id="sched-slot" value={slotMinutes} onChange={(e) => setSlotMinutes(e.target.value)}>
                {[15, 20, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="sched-conc">Max concurrent</label>
              <input
                id="sched-conc"
                type="number"
                min={1}
                max={20}
                value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="sched-active">Availability</label>
              <label htmlFor="sched-active" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, paddingTop: 6 }}>
                <input
                  id="sched-active"
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                Active
              </label>
            </div>
          </div>

          {!active && (
            <div className="mono text-xs" style={{ color: 'var(--warning)' }}>
              Inactive schedules generate no slots — this consultant won't be bookable.
            </div>
          )}
          {active && enabledDays.length === 0 && (
            <div className="mono text-xs" style={{ color: 'var(--warning)' }}>
              No working days selected — this consultant won't be bookable until you enable at least one day.
            </div>
          )}

          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSave}>
            {busy ? 'Saving…' : 'Save working hours'}
          </button>
        </div>
      </form>
    </div>
  );
}
